'use client';

import { useState, useRef } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';
import { APP_URL, resolveMediaUrl } from '@/lib/api';
import FeaturesDropdown from '@/components/FeaturesDropdown';

// V1.25.938 — Suppression TOTALE du refetch client (getVitrineSettings/Pages).
// Cause racine du bug "1 clic sur 2" : meme avec JSON.stringify guard, le moindre
// changement (admin modifie 1 byte) declenchait un setState → re-render Navbar →
// les <a> etaient demontes/remontes. Si l'utilisateur cliquait pendant la fenetre
// de remount (50-150ms), le clic etait perdu.
//
// Maintenant : la Navbar n'a PLUS de useEffect au mount. Elle se base 100% sur
// les props server-rendered. Pour les updates admin, l'auto-rebuild watcher
// regenere la vitrine en ~90s (poll 30s + build 60s) → les modifs apparaissent
// au prochain refresh. Trade-off acceptable car les updates admin ne sont pas
// frequents et l'utilisateur final voit un site PARFAITEMENT clickable au 1er coup.

// Detecte si un item du menu doit ouvrir un sous-menu (dropdown).
// Source de verite : flag explicite `submenu_type` ('features' ou 'custom')
// configure dans l'admin Header → "Sous-menu au survol".
function hasSubmenu(item) {
  return !!(item && (item.submenu_type === 'features' || item.submenu_type === 'custom'));
}

// Cle stable d'une feature — DOIT matcher FeaturesDropdown / admin VitrineHeader.
function featureKey(f) {
  if (!f) return null;
  if (f.id) return f.id;
  if (f.title) return 't:' + String(f.title).trim().toLowerCase();
  return null;
}

// V1.25.1403 — Resout les items d'un sous-menu (memes regles que
// FeaturesDropdown) pour pouvoir les afficher en ACCORDEON dans le menu
// mobile : sur mobile il n'y a pas de survol, le dropdown desktop est donc
// inutilisable. On deplie les items directement sous l'item parent.
function resolveSubmenuItems(link, settings) {
  if (!link) return [];
  if (link.submenu_type === 'custom') {
    return (link.submenu_items || [])
      .filter(it => it && (it.label || it.url))
      .map(it => ({ label: it.label || 'Lien', url: it.url || '#', target: it.target || '_self' }));
  }
  // mode "features"
  const features = (settings?.features || []).filter(f => f && f.active !== false);
  const ids = Array.isArray(link.submenu_feature_ids) ? link.submenu_feature_ids : [];
  const filtered = ids.length > 0
    ? features.filter(f => ids.includes(featureKey(f)))
    : features;
  const items = filtered.map(f => ({
    label: f.title || 'Fonctionnalité',
    url: f.url || '/fonctionnalites',
    target: f.target || '_self',
  }));
  items.push({ label: 'Voir toutes les fonctionnalités', url: '/fonctionnalites', target: '_self' });
  return items;
}

export default function Navbar({ settings, initialCmsPages = [], isHomePage = false }) {
  // V1.25.938 — settings vient directement des props (server-rendered).
  // Plus de useState/useEffect au mount → plus de re-render → clics fiables.
  const [mobileOpen, setMobileOpen] = useState(false);
  // V1.25.1403 — index du sous-menu mobile actuellement deplie (accordeon).
  const [expandedSubmenu, setExpandedSubmenu] = useState(null);
  // ── Dropdown "Fonctionnalites" — ouvert au survol.
  // closeTimer est un useRef (PAS un useState) pour eviter qu'un onMouseLeave
  // sur un item du menu declenche un re-render pendant que l'utilisateur clique
  // sur un autre lien (cause potentielle du bug "1 clic sur 2").
  const [featuresDropdownOpen, setFeaturesDropdownOpen] = useState(false);
  const closeTimerRef = useRef(null);
  const openDropdown = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    // Skip setState si deja ouvert → pas de re-render inutile.
    if (!featuresDropdownOpen) setFeaturesDropdownOpen(true);
  };
  const scheduleCloseDropdown = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => setFeaturesDropdownOpen(false), 180);
  };
  // initialCmsPages utilise directement (plus de state/setState).
  const cmsPages = initialCmsPages;
  // ── isHomePage : passe en PROP par le parent (HomePageContent=true,
  // CmsPageContent=false). On ne peut PAS utiliser usePathname() ici car ca
  // cause un hydration mismatch React #418 / #423 en static export.
  void isHomePage; // reserve pour usage futur (header transparent home)

  const logoUrl = resolveMediaUrl(settings?.logo_url);
  const headerCfg = settings?.header || {};

  // ── Apparence : couleur de fond + transparence ────────────────────────
  // hexToRgba(hex, alpha[0..1]) -> string CSS rgba(r,g,b,a)
  const hexToRgba = (hex, alpha) => {
    if (!hex || typeof hex !== 'string') return `rgba(255,255,255,${alpha})`;
    let h = hex.replace('#', '').trim();
    if (h.length === 3) h = h.split('').map(c => c + c).join('');
    if (h.length !== 6) return `rgba(255,255,255,${alpha})`;
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return `rgba(${r},${g},${b},${alpha})`;
  };

  const bgColor = headerCfg.bg_color || '#ffffff';
  const bgOpacity = Math.max(0, Math.min(100, Number.isFinite(+headerCfg.bg_opacity) ? +headerCfg.bg_opacity : 95)) / 100;
  const bgBlur = Math.max(0, Math.min(30, Number.isFinite(+headerCfg.bg_blur) ? +headerCfg.bg_blur : 12));
  const showBorder = headerCfg.border_bottom !== false;
  const linkColor = headerCfg.text_color || 'var(--color-text)';

  // ─── HEADER TOUJOURS OPAQUE
  const bgMode = 'always';

  // ─── ELIMINATION DU FLASH DE COULEUR (V1.25.911)
  // Probleme : le HTML SSG est buildé avec la couleur d'admin du moment du
  // build (ex: rouge). Si l'user change admin pour blanc et refresh la page,
  // le navigateur affiche d'abord rouge (SSG), puis le client useEffect
  // refetch les settings, React re-render avec blanc → FLASH visible.
  //
  // Solution : on n'ecrit PLUS la couleur computee dans le style inline.
  // On utilise une CSS variable `--ligobox-header-bg` que ThemeInjector
  // met a jour instantanement quand l'API renvoie une nouvelle valeur.
  // Le navigateur repaint automatiquement quand la variable change → pas
  // de re-render React → pas de fenetre de "wrong color" → pas de flash.
  //
  // Le SSG HTML contient maintenant juste `background: var(--ligobox-header-bg)`.
  // La valeur de la variable est posee par layout.jsx (default blanc) puis
  // override par ThemeInjector si l'API renvoie autre chose.
  // bgMode est forcé à 'always', donc showBg est toujours true.
  const showBg = bgMode === 'always';
  const headerBg = showBg ? 'var(--ligobox-header-bg, rgba(255,255,255,0.95))' : 'transparent';
  const headerBlur = showBg && bgBlur > 0 ? `saturate(180%) blur(${bgBlur}px)` : 'none';
  const headerBorder = showBg && showBorder ? '1px solid var(--color-border)' : '1px solid transparent';

  // hexToRgba reste utilise par d'autres parties du composant (futures
  // extensions). Pas pour bgColor qui passe maintenant par CSS variable.
  void hexToRgba; void bgColor; void bgOpacity;

  // Helper : normalise une URL pour qu'une ancre (#xxx) pointe TOUJOURS
  // vers la page d'accueil (/#xxx) — sinon depuis une page CMS comme
  // /conception-electrique, le lien #features serait relatif à cette page
  // et ne ferait rien.
  const normalizeUrl = (url) => {
    if (!url) return '#';
    if (url.startsWith('#')) return '/' + url;
    return url;
  };

  const customItems = Array.isArray(headerCfg.items) ? headerCfg.items : [];
  const fallbackItems = [
    { label: 'Fonctionnalites', url: '/#features', target: '_self' },
    { label: 'Tarifs', url: '/#packs', target: '_self' },
    { label: 'Contact', url: '/#contact', target: '_self' },
  ];
  const baseItems = customItems.length ? customItems : fallbackItems;
  const links = [
    ...baseItems.map(it => ({
      href: normalizeUrl(it.url),
      label: it.label || '',
      target: it.target || '_self',
      submenu_type: it.submenu_type || 'none',
      submenu_feature_ids: Array.isArray(it.submenu_feature_ids) ? it.submenu_feature_ids : [],
      submenu_items: Array.isArray(it.submenu_items) ? it.submenu_items : [],
    })),
    ...cmsPages.map(p => ({ href: `/${p.slug}`, label: p.title, target: '_self' })),
  ];

  // Boutons login/signup configurables
  const showLogin   = headerCfg.show_login_button   !== false;
  const showSignup  = headerCfg.show_signup_button  !== false;
  const loginText   = headerCfg.login_button_text   || 'Se connecter';
  const loginUrl    = headerCfg.login_button_url    || `${APP_URL}/admin-login`;
  const signupText  = headerCfg.signup_button_text  || 'Essayer gratuitement';
  // Par défaut, le bouton « Essayer gratuitement » ouvre la page de création de compte.
  const signupUrl   = normalizeUrl(headerCfg.signup_button_url || `${APP_URL}/signup`);

  return (
    <header
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 50,
        background: headerBg,
        backdropFilter: headerBlur,
        WebkitBackdropFilter: headerBlur,
        borderBottom: headerBorder,
        transition: 'all 0.2s',
      }}
    >
      <div className="container-page" style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: '72px',
      }}>
        {/* Logo */}
        {/* Logo cliquable → ramene a la homepage. Avant href="#" qui ne
            faisait rien — bug signale par user le 05/05/2026. */}
        <a href="/" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={settings?.site_title || 'Ligobox'}
              style={{ height: '36px', maxWidth: '160px', objectFit: 'contain' }}
            />
          ) : (
            <span style={{
              fontSize: '22px',
              fontWeight: 800,
              color: 'var(--color-primary)',
              letterSpacing: '-0.02em',
            }}>
              {settings?.site_title || 'Ligobox'}
            </span>
          )}
        </a>

        {/* Liens desktop — V1.25.1401 : PAS de display:flex inline, sinon il
            écrase la classe `hidden` et le menu desktop reste visible en
            mobile. Le display est piloté uniquement par `hidden md:flex`. */}
        <nav style={{ alignItems: 'center', gap: '32px' }} className="lb-nav-desktop hidden md:flex">
          {links.map((link, i) => {
            // Si l'item a un sous-menu configure (features ou custom) → wrap
            // dans un container qui gere l'ouverture du dropdown au survol.
            if (hasSubmenu(link)) {
              return (
                <div
                  key={`${link.href}-${i}`}
                  style={{ position: 'relative', display: 'inline-flex' }}
                  onMouseEnter={openDropdown}
                  onMouseLeave={scheduleCloseDropdown}
                >
                  <a
                    href={link.href}
                    target={link.target}
                    rel={link.target === '_blank' ? 'noopener noreferrer' : undefined}
                    style={{
                      fontSize: '15px',
                      fontWeight: 500,
                      color: linkColor,
                      transition: 'color 0.15s',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                    onMouseEnter={e => e.currentTarget.style.color = 'var(--color-primary)'}
                    onMouseLeave={e => e.currentTarget.style.color = linkColor}
                  >
                    {link.label}
                    <ChevronDown size={14} style={{
                      transform: featuresDropdownOpen ? 'rotate(180deg)' : 'none',
                      transition: 'transform 0.15s',
                    }} />
                  </a>
                  {featuresDropdownOpen && (
                    <FeaturesDropdown
                      mode={link.submenu_type}
                      features={settings?.features || []}
                      filterIds={link.submenu_feature_ids || []}
                      customItems={link.submenu_items || []}
                      onItemClick={() => setFeaturesDropdownOpen(false)}
                    />
                  )}
                </div>
              );
            }
            // Item standard
            return (
            <a
              key={`${link.href}-${i}`}
              href={link.href}
              target={link.target}
              rel={link.target === '_blank' ? 'noopener noreferrer' : undefined}
              style={{
                fontSize: '15px',
                fontWeight: 500,
                color: linkColor,
                transition: 'color 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.color = 'var(--color-primary)'}
              onMouseLeave={e => e.currentTarget.style.color = linkColor}
            >
              {link.label}
            </a>
            );
          })}
        </nav>

        {/* CTAs desktop — V1.25.1401 : idem, pas de display:flex inline. */}
        <div style={{ alignItems: 'center', gap: '12px' }} className="lb-nav-desktop hidden md:flex">
          {showLogin && (
            <a
              href={loginUrl}
              style={{
                fontSize: '15px', fontWeight: 500,
                color: 'var(--color-text)', padding: '10px 16px',
              }}
            >
              {loginText}
            </a>
          )}
          {showSignup && (
            <a href={signupUrl} className="btn-primary" style={{ padding: '10px 20px', fontSize: '15px' }}>
              {signupText}
            </a>
          )}
        </div>

        {/* Burger mobile */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="lb-nav-burger md:hidden"
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: '8px',
          }}
          aria-label="Menu"
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Menu mobile */}
      {mobileOpen && (
        <div className="lb-nav-mobile md:hidden" style={{
          background: '#fff',
          borderTop: '1px solid var(--color-border)',
          padding: '16px 24px',
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
          maxHeight: 'calc(100vh - 72px)',
          overflowY: 'auto',
        }}>
          {links.map((link, i) => {
            // V1.25.1403 — Lien AVEC sous-menu : accordéon. Sur mobile il n'y
            // a pas de survol, donc on déplie les items au tap sur le parent.
            if (hasSubmenu(link)) {
              const subItems = resolveSubmenuItems(link, settings);
              const isExpanded = expandedSubmenu === i;
              return (
                <div key={`${link.href}-${i}`}>
                  <button
                    type="button"
                    onClick={() => setExpandedSubmenu(isExpanded ? null : i)}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '12px',
                      fontSize: '16px',
                      fontWeight: 500,
                      background: 'transparent',
                      border: 'none',
                      borderBottom: '1px solid var(--color-border)',
                      cursor: 'pointer',
                      color: 'var(--color-text)',
                      textAlign: 'left',
                    }}
                    aria-expanded={isExpanded}
                  >
                    {link.label}
                    <ChevronDown size={18} style={{
                      transform: isExpanded ? 'rotate(180deg)' : 'none',
                      transition: 'transform 0.15s',
                      flex: '0 0 auto',
                    }} />
                  </button>
                  {isExpanded && subItems.map((it, j) => (
                    <a
                      key={j}
                      href={it.url}
                      target={it.target}
                      rel={it.target === '_blank' ? 'noopener noreferrer' : undefined}
                      onClick={() => { setMobileOpen(false); setExpandedSubmenu(null); }}
                      style={{
                        display: 'block',
                        padding: '10px 12px 10px 28px',
                        fontSize: '15px',
                        fontWeight: 500,
                        color: 'var(--color-text-muted)',
                        borderBottom: '1px solid var(--color-border)',
                      }}
                    >
                      {it.label}
                    </a>
                  ))}
                </div>
              );
            }
            // Lien standard
            return (
              <a
                key={`${link.href}-${i}`}
                href={link.href}
                target={link.target}
                rel={link.target === '_blank' ? 'noopener noreferrer' : undefined}
                onClick={() => { setMobileOpen(false); setExpandedSubmenu(null); }}
                style={{
                  padding: '12px',
                  fontSize: '16px',
                  fontWeight: 500,
                  borderBottom: '1px solid var(--color-border)',
                }}
              >
                {link.label}
              </a>
            );
          })}
          {showLogin && (
            <a href={loginUrl}
              style={{ padding: '12px', fontSize: '16px', fontWeight: 500 }}>
              {loginText}
            </a>
          )}
          {showSignup && (
            <a href={signupUrl}
              onClick={() => setMobileOpen(false)}
              className="btn-primary"
              style={{ marginTop: '8px', justifyContent: 'center' }}>
              {signupText}
            </a>
          )}
        </div>
      )}
    </header>
  );
}

// (mergeSettingsKeepFull supprime le 14/05/2026)
// L'ancien merge "preservait" la liste la plus longue → empechait les
// suppressions legitimes : si admin retirait un item du menu, le merge gardait
// la version avec l'item parce que "elle est plus longue donc plus correcte".
// Resultat : decocher "Dans le footer" sur une page ne supprimait pas la
// page du footer apres refresh. L'API est SOURCE DE VERITE — on remplace
// directement avec ce qu'elle dit.
